import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  PhoneIphone as AppIcon,
  Psychology as AIIcon,
  LocalOffer as PromoIcon,
  Build as MaintenanceIcon,
  Business as BusinessIcon,
  AccountCircle,
  Logout,
  Person,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon,
  ScienceOutlined as DemoIcon,
  Language as LanguageIcon,
  RecordVoiceOver as VoiceIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

// Route-to-page-title mapping
const getPageTitle = (pathname) => {
  const routeMap = {
    '/overview': 'Overview',
    '/companies': 'Company Management',
    '/subdomains': 'Subdomain Management',
    '/storage-analytics': 'Storage Analytics',
    '/pricing-configuration': 'Tier Limits',
    '/configuration': 'Configuration',
    '/ai-management': 'AI Feature Configuration',
    '/promotions': 'Promotional Tier Codes',
    '/notifications': 'Admin Notifications',
    '/tier-upgrades': 'Temporary Tier Upgrades',
    '/voice-failures': 'Novo Voice Failed Commands',
    '/crash-reports': 'Crash Reports',
    '/system': 'System Maintenance',
    '/settings': 'Settings',
    '/demo-settings': 'Demo Mode Settings',
  };
  return routeMap[pathname] || 'Business Intelligence Portal';
};

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const pageTitle = getPageTitle(location.pathname);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  // Consolidated menu structure
  const menuItems = [
    {
      text: 'Overview',
      icon: <DashboardIcon />,
      path: '/overview',
    },
    {
      text: 'Companies',
      icon: <BusinessIcon />,
      path: '/companies',
    },
    {
      text: 'Subdomains',
      icon: <LanguageIcon />,
      path: '/subdomains',
    },
    {
      text: 'Storage Analytics',
      icon: <StorageIcon />,
      path: '/storage-analytics',
    },
    {
      text: 'Tier Limits',
      icon: <StorageIcon />,
      path: '/pricing-configuration',
    },
    {
      text: 'Configuration',
      icon: <AppIcon />,
      path: '/configuration',
      title: 'App features, Hosted AI (Features & Limits), legal, Stripe, etc.',
    },
    {
      text: 'AI Management',
      icon: <AIIcon />,
      path: '/ai-management',
    },
    {
      text: 'Promotions',
      icon: <PromoIcon />,
      path: '/promotions',
    },
    {
      text: 'Notifications',
      icon: <NotificationsIcon />,
      path: '/notifications',
    },
    {
      text: 'Tier Upgrades',
      icon: <TrendingUpIcon />,
      path: '/tier-upgrades',
    },
    {
      text: 'Voice Failed Commands',
      icon: <VoiceIcon />,
      path: '/voice-failures',
    },
    {
      text: 'Crash Reports',
      icon: <BugIcon />,
      path: '/crash-reports',
    },
    {
      text: 'System',
      icon: <MaintenanceIcon />,
      path: '/system',
    },
    {
      text: 'Demo Mode',
      icon: <DemoIcon />,
      path: '/demo-settings',
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
    },
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Novo BI Portal
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              title={item.title}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

	  return (
	    <Box sx={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
	          // Avoid calc(width) + margins (can produce subtle overflow on pages with vertical scrollbars)
	          left: { md: drawerWidth },
	          right: 0,
	          width: { md: 'auto' },
	          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {pageTitle}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {admin?.username}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 }, // Responsive padding
          mt: 8, // Account for AppBar height
          minWidth: 0, // Prevent flex child from expanding beyond viewport
          overflowX: 'hidden', // Contain any overflowing content
          boxSizing: 'border-box',
          maxWidth: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
